import { useNavigate } from 'react-router-dom';

interface RoleCard {
  emoji: string;
  title: string;
  subtitle: string;
  route: string;
  accentColor: string;
  buttonVariant: 'orange-solid' | 'dark-solid' | 'orange-outline';
}

const roles: RoleCard[] = [
  {
    emoji: '🏗️',
    title: 'Management',
    subtitle: 'Project managers, admin & site staff',
    route: '/login/management',
    accentColor: '#F05A00',
    buttonVariant: 'orange-solid',
  },
  {
    emoji: '🔧',
    title: 'Subcontractor',
    subtitle: 'Trade contractors and site workers',
    route: '/login/subcontractor',
    accentColor: '#2D2D2D',
    buttonVariant: 'dark-solid',
  },
  {
    emoji: '🏠',
    title: 'Client Portal',
    subtitle: 'Homeowners and project clients',
    route: '/login/client',
    accentColor: '#F05A00',
    buttonVariant: 'orange-outline',
  },
];

export default function LoginSelectPage() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
      style={{ background: '#F7F6F3' }}
    >
      {/* Subtle grid texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#EEECE8 1px, transparent 1px),
            linear-gradient(90deg, #EEECE8 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          opacity: 0.6,
        }}
      />

      <div className="relative z-10 w-full flex flex-col items-center">
        {/* Logo Area */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
            alt="JPH Portal"
            style={{
              maxWidth: '280px',
              width: '100%',
              objectFit: 'contain',
            }}
          />
          <p
            className="mt-4 text-center"
            style={{
              color: '#999999',
              fontSize: '12px',
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              fontFamily: 'Inter, DM Sans, sans-serif',
            }}
          >
            Construction Management Platform
          </p>
          <div
            style={{
              width: '48px',
              height: '2px',
              background: '#F05A00',
              margin: '24px auto 0',
              borderRadius: '2px',
            }}
          />
        </div>

        {/* Role Cards */}
        <div
          className="w-full grid grid-cols-1 md:grid-cols-3 gap-5"
          style={{ maxWidth: '860px' }}
        >
          {roles.map((role) => (
            <RoleCard
              key={role.route}
              role={role}
              onSelect={() => navigate(role.route)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 flex flex-col items-center gap-1.5">
          <p
            className="text-center"
            style={{
              color: '#AAAAAA',
              fontSize: '12px',
              fontFamily: 'Inter, DM Sans, sans-serif',
            }}
          >
            Need access? Contact your Japan Homes project manager.
          </p>
          <p
            className="text-center"
            style={{
              color: '#CCCCCC',
              fontSize: '11px',
              fontFamily: 'Inter, DM Sans, sans-serif',
            }}
          >
            Produced by Japan Homes Group&nbsp;
          </p>
        </div>
      </div>
    </div>
  );
}

function RoleCard({
  role,
  onSelect,
}: {
  role: RoleCard;
  onSelect: () => void;
}) {
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.style.boxShadow = '0 6px 24px rgba(240,90,0,0.15)';
    el.style.borderColor = '#F05A00';
    el.style.transform = 'translateY(-3px)';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)';
    el.style.borderColor = '#E5E2DC';
    el.style.transform = 'translateY(0)';
  };

  return (
    <div
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="cursor-pointer flex flex-col"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E2DC',
        borderRadius: '14px',
        padding: '0 0 28px 0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        transition: 'box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
        overflow: 'hidden',
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          height: '4px',
          background: role.accentColor,
          borderRadius: '14px 14px 0 0',
          flexShrink: 0,
        }}
      />

      <div className="flex flex-col flex-1 px-7 pt-6">
        {/* Icon + Title inline */}
        <div className="flex items-center gap-3 mb-2">
          <span style={{ fontSize: '32px', lineHeight: 1, flexShrink: 0 }}>{role.emoji}</span>
          <h2
            style={{
              color: '#1A1A1A',
              fontSize: '18px',
              fontWeight: 700,
              fontFamily: 'Inter, DM Sans, sans-serif',
              margin: 0,
            }}
          >
            {role.title}
          </h2>
        </div>

        {/* Subtitle */}
        <p
          style={{
            color: '#888888',
            fontSize: '13px',
            fontFamily: 'Inter, DM Sans, sans-serif',
            marginTop: '6px',
            marginBottom: '20px',
            lineHeight: '1.5',
            flexGrow: 1,
          }}
        >
          {role.subtitle}
        </p>

        {/* Button */}
        <ActionButton variant={role.buttonVariant} />
      </div>
    </div>
  );
}

function ActionButton({ variant }: { variant: RoleCard['buttonVariant'] }) {
  const baseStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Inter, DM Sans, sans-serif',
    cursor: 'pointer',
    transition: 'background 0.2s ease, border-color 0.2s ease',
    textAlign: 'center',
    display: 'block',
    whiteSpace: 'nowrap',
  };

  if (variant === 'orange-solid') {
    return (
      <span
        style={{
          ...baseStyle,
          background: '#F05A00',
          color: '#FFFFFF',
          border: 'none',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = '#D94F00';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = '#F05A00';
        }}
      >
        Enter Portal →
      </span>
    );
  }

  if (variant === 'dark-solid') {
    return (
      <span
        style={{
          ...baseStyle,
          background: '#2D2D2D',
          color: '#FFFFFF',
          border: 'none',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = '#444444';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = '#2D2D2D';
        }}
      >
        Enter Portal →
      </span>
    );
  }

  // orange-outline
  return (
    <span
      style={{
        ...baseStyle,
        background: '#FFFFFF',
        color: '#F05A00',
        border: '2px solid #F05A00',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = '#FFF3EE';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = '#FFFFFF';
      }}
    >
      Enter Portal →
    </span>
  );
}
